import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const logoUrl = '/logo.png';

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-lg backdrop-blur-md' : ''
      }`}
      style={{
        backgroundColor: isScrolled ? 'rgba(240, 224, 208, 0.95)' : '#F0E0D0'
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center transition-transform duration-300 hover:scale-105">
            <img
              src={logoUrl}
              alt="TheApostrophe - Global Regulatory Affairs Experts"
              className="h-12 w-auto"
              style={{ filter: 'brightness(0) saturate(100%)' }}
              loading="eager"
              width="120"
              height="48"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className="font-medium transition-colors duration-200 hover:opacity-70"
              style={{ color: location.pathname === '/' ? '#606090' : '#303040' }}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="font-medium transition-colors duration-200 hover:opacity-70"
              style={{ color: location.pathname === '/about' ? '#606090' : '#303040' }}
            >
              About
            </Link>
            <Link
              to="/services"
              className="font-medium transition-colors duration-200 hover:opacity-70"
              style={{ color: location.pathname === '/services' ? '#606090' : '#303040' }}
            >
              Services
            </Link>
            <Link
              to="/launch-program"
              className="font-medium transition-colors duration-200 hover:opacity-70"
              style={{ color: location.pathname === '/launch-program' ? '#606090' : '#303040' }}
            >
              Launch Program
            </Link>
            
            {/* Success Packs Dropdown */}
            <div className="relative group">
              <Link
                to="/success-packs"
                className="font-medium transition-colors duration-200 hover:opacity-70 flex items-center gap-1"
                style={{ color: location.pathname.includes('/success-packs') ? '#606090' : '#303040' }}
              >
                Success Packs
                <ChevronDown className="h-4 w-4" />
              </Link>
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link
                    to="/success-packs"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold">All Success Packs</div>
                    <div className="text-sm opacity-70">Overview of all packs</div>
                  </Link>
                  <div className="my-2 border-t" style={{ borderColor: '#E0D0C0' }}></div>
                  <Link
                    to="/success-packs/sourcing-partnership-pack"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold" style={{ color: '#A07050' }}>Sourcing & Partnership Pack</div>
                    <div className="text-sm opacity-70">Manufacturer & importer sourcing</div>
                  </Link>
                  <Link
                    to="/success-packs/groundwork-pack"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold" style={{ color: '#606090' }}>Groundwork Pack</div>
                    <div className="text-sm opacity-70">Market entry feasibility</div>
                  </Link>
                  <Link
                    to="/success-packs/gap-analysis-pack"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold" style={{ color: '#7090A0' }}>GAP Analysis Pack</div>
                    <div className="text-sm opacity-70">Pre-submission review</div>
                  </Link>
                  <Link
                    to="/success-packs/registration-dossier-pack"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold" style={{ color: '#A060B0' }}>Registration & Dossier Pack</div>
                    <div className="text-sm opacity-70">Complete documentation</div>
                  </Link>
                  <Link
                    to="/success-packs/lifecycle-pack"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold" style={{ color: '#708080' }}>Lifecycle Support Pack</div>
                    <div className="text-sm opacity-70">Post-approval compliance</div>
                  </Link>
                </div>
              </div>
            </div>
            
            <Link
              to="/retainer"
              className="font-medium transition-colors duration-200 hover:opacity-70"
              style={{ color: location.pathname === '/retainer' ? '#A060B0' : '#303040' }}
            >
              Retainer Plan
            </Link>
            
            {/* Regions Dropdown */}
            <div className="relative group">
              <button className="font-medium transition-colors duration-200 hover:opacity-70 flex items-center gap-1" style={{ color: '#303040' }}>
                Regions
                <ChevronDown className="h-4 w-4" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link
                    to="/regions/africa"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold">Africa</div>
                    <div className="text-sm opacity-70">East & West African Markets</div>
                  </Link>
                  <Link
                    to="/regions/asia"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold">Asia</div>
                    <div className="text-sm opacity-70">South & Southeast Asia</div>
                  </Link>
                  <Link
                    to="/regions/gcc"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold">GCC</div>
                    <div className="text-sm opacity-70">Gulf Cooperation Council</div>
                  </Link>
                  <Link
                    to="/regions/latam"
                    className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
                    style={{ color: '#303040' }}
                  >
                    <div className="font-semibold">Latin America</div>
                    <div className="text-sm opacity-70">Central & South America</div>
                  </Link>
                </div>
              </div>
            </div>
            
            <Link
              to="/contact"
              className="font-medium transition-colors duration-200 hover:opacity-70"
              style={{ color: location.pathname === '/contact' ? '#606090' : '#303040' }}
            >
              Contact Us
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button
                className="px-6 py-2 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg"
                style={{ backgroundColor: '#606090', color: 'white' }}
              >
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-3"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" style={{ color: '#303040' }} />
            ) : (
              <Menu className="h-6 w-6" style={{ color: '#303040' }} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-6 space-y-4 border-t" style={{ borderColor: '#708080' }}>
            <Link
              to="/"
              className="block py-2 font-medium transition-colors duration-200"
              style={{ color: '#303040' }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block py-2 font-medium transition-colors duration-200"
              style={{ color: '#303040' }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About
            </Link>
            <Link
              to="/services"
              className="block py-2 font-medium transition-colors duration-200"
              style={{ color: '#303040' }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Services
            </Link>
            <Link
              to="/launch-program"
              className="block py-2 font-medium transition-colors duration-200"
              style={{ color: '#303040' }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Launch Program
            </Link>
            <div className="py-2">
              <div className="font-medium mb-2" style={{ color: '#303040' }}>Success Packs</div>
              <div className="pl-4 space-y-2">
                <Link
                  to="/success-packs"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  All Success Packs
                </Link>
                <Link
                  to="/success-packs/sourcing-partnership-pack"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sourcing & Partnership Pack
                </Link>
                <Link
                  to="/success-packs/groundwork-pack"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Groundwork Pack
                </Link>
                <Link
                  to="/success-packs/gap-analysis-pack"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  GAP Analysis Pack
                </Link>
                <Link
                  to="/success-packs/registration-dossier-pack"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Registration & Dossier Pack
                </Link>
                <Link
                  to="/success-packs/lifecycle-pack"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Lifecycle Support Pack
                </Link>
              </div>
            </div>
            <Link
              to="/retainer"
              className="block py-2 font-medium transition-colors duration-200"
              style={{ color: '#A060B0' }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Retainer Plan
            </Link>
            <div className="py-2">
              <div className="font-medium mb-2" style={{ color: '#303040' }}>Regions</div>
              <div className="pl-4 space-y-2">
                <Link
                  to="/regions/africa"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Africa
                </Link>
                <Link
                  to="/regions/asia"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Asia
                </Link>
                <Link
                  to="/regions/gcc"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  GCC
                </Link>
                <Link
                  to="/regions/latam"
                  className="block py-1 text-sm transition-colors duration-200"
                  style={{ color: '#505060' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Latin America
                </Link>
              </div>
            </div>
            <Link
              to="/contact"
              className="block py-2 font-medium transition-colors duration-200"
              style={{ color: '#303040' }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact Us
            </Link>
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button
                className="w-full mt-4 rounded-full"
                style={{ backgroundColor: '#606090', color: 'white' }}
              >
                Get Started
              </Button>
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};
